// ============================================================
// FILE: src/components/Layout/Layout.jsx
// PURPOSE: The main shell — sidebar + topbar + page content
//          Outlet renders the current page inside
//          Now supports Client role with separate nav items
// ============================================================

import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationsAPI } from '../../api/services';
import './Layout.css';

// ── Staff / Admin Navigation ───────────────────────────────
const STAFF_NAV = [
  {
    section: 'Main',
    items: [
      { path: '/',             label: 'Dashboard',    icon: '📊', end: true },
      { path: '/clients',      label: 'Clients',      icon: '👥' },
      { path: '/caregivers',   label: 'Caregivers',   icon: '👨‍⚕️' },
      { path: '/visits',       label: 'Visits',       icon: '📅' },
      { path: '/attendance',   label: 'Attendance',   icon: '⏰' },
    ]
  },
  {
    section: 'Clinical',
    items: [
      { path: '/careplans',    label: 'Care Plans',   icon: '📋' },
      { path: '/documents',    label: 'Documents',    icon: '📄' },
      { path: '/medicaid',     label: 'Medicaid Auth',icon: '🏥' },
    ]
  },
  {
    section: 'Finance',
    items: [
      { path: '/billing',      label: 'Billing',      icon: '💰' },
      { path: '/payroll',      label: 'Payroll',      icon: '💵' },
    ]
  },
  {
    section: 'Management',
    items: [
      { path: '/training',      label: 'Training',     icon: '📚' },
      { path: '/notifications', label: 'Notifications',icon: '🔔' },
      { path: '/users',         label: 'Users',        icon: '⚙️', adminOnly: true },
    ]
  },
];

// ── Client Portal Navigation ───────────────────────────────
const CLIENT_NAV = [
  {
    section: 'My Portal',
    items: [
      { path: '/client/dashboard',   label: 'Dashboard',     icon: '🏠', end: true },
      { path: '/client/visits',      label: 'My Visits',     icon: '📅' },
      { path: '/client/caregivers',  label: 'My Caregivers', icon: '🧑‍⚕️' },
      { path: '/client/attendance',  label: 'Activity',      icon: '⏱️' },
      { path: '/client/careplan',    label: 'Care Plan',     icon: '📋' },
    ]
  },
];

// ── Page title map ─────────────────────────────────────────
const getPageInfo = (path, user) => {
  const firstName = user?.firstName || user?.FirstName || '';

  // Client portal pages
  if (path === '/client/dashboard')  return { title: 'My Dashboard',    sub: `Welcome back, ${firstName}!` };
  if (path.startsWith('/client/visits'))     return { title: 'My Visits',     sub: 'Your caregiver visit schedule' };
  if (path.startsWith('/client/caregivers')) return { title: 'My Caregivers', sub: 'Your assigned care team' };
  if (path.startsWith('/client/attendance')) return { title: 'Activity',      sub: 'Caregiver clock-in and clock-out records' };
  if (path.startsWith('/client/careplan'))   return { title: 'Care Plan',     sub: 'Your active care plan' };

  // Staff pages
  if (path === '/')                          return { title: 'Dashboard',             sub: `Welcome back, ${firstName}! Here's your overview` };
  if (path.startsWith('/clients'))           return { title: 'Clients',              sub: 'Manage client records and care plans' };
  if (path.startsWith('/caregivers'))        return { title: 'Caregivers',           sub: 'Manage your care team' };
  if (path.startsWith('/visits'))            return { title: 'Visits',              sub: 'Schedule and track client visits' };
  if (path.startsWith('/attendance'))        return { title: 'Attendance',           sub: 'Clock-in/out logs and timesheets' };
  if (path.startsWith('/billing'))           return { title: 'Billing',             sub: 'Invoices and payment management' };
  if (path.startsWith('/payroll'))           return { title: 'Payroll',             sub: 'Caregiver pay and hours' };
  if (path.startsWith('/careplans'))         return { title: 'Care Plans',          sub: 'Client care documentation' };
  if (path.startsWith('/training'))          return { title: 'Training',            sub: 'Certifications and compliance' };
  if (path.startsWith('/documents'))         return { title: 'Documents',           sub: 'File uploads and records' };
  if (path.startsWith('/medicaid'))          return { title: 'Medicaid Authorizations', sub: 'Monthly hour tracking' };
  if (path.startsWith('/users'))             return { title: 'Users',               sub: 'System user management' };
  if (path.startsWith('/notifications'))     return { title: 'Notifications',       sub: 'Alerts and messages' };
  return { title: 'EternalCare', sub: '' };
};

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [unread, setUnread] = useState(0);

  // Determine if this user is a Client
  const isClient = (user?.role || user?.Role) === 'Client';

  // Pick the correct nav based on role
  const NAV = isClient ? CLIENT_NAV : STAFF_NAV;

  // Fetch unread notification count every 60 seconds
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await notificationsAPI.getUnreadCount();
        setUnread(res.data.unreadCount || res.data.count || 0);
      } catch (_) {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, []);

  const { title, sub } = getPageInfo(location.pathname, user);

  const initials = user
    ? `${(user.firstName || user.FirstName || '')[0] || ''}${(user.lastName || user.LastName || '')[0] || ''}`.toUpperCase()
    : 'U';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Notification path differs for client vs staff
  const notifPath = isClient ? '/client/notifications' : '/notifications';

  return (
    <div className="layout-shell">

      {/* ── Sidebar ── */}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">{isClient ? '🏠' : '🏥'}</div>
          <div>
            <div className="sidebar-logo-text">EternalCare</div>
            <div className="sidebar-logo-sub">
              {isClient ? 'Client Portal' : 'Home Care System'}
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {NAV.map(({ section, items }) => (
            <div key={section}>
              <div className="nav-section-label">{section}</div>
              {items
                .filter(item => !item.adminOnly || hasRole('Admin'))
                .map(item => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.end}
                    className={({ isActive }) =>
                      `nav-item${isActive ? ' active' : ''}`
                    }
                  >
                    <span className="nav-item-icon">{item.icon}</span>
                    <span>{item.label}</span>
                    {/* Notification badge — show on notification nav item */}
                    {(item.path === '/notifications' || item.path === '/client/notifications') && unread > 0 && (
                      <span style={{
                        marginLeft: 'auto',
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: '99px',
                      }}>
                        {unread}
                      </span>
                    )}
                  </NavLink>
                ))
              }
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          {/* Role badge for client */}
          {isClient && (
            <div style={{
              textAlign: 'center',
              marginBottom: 12,
              background: '#ede9fe',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#7c3aed',
            }}>
              👤 Client Portal
            </div>
          )}
          <div className="sidebar-user">
            <div className="sidebar-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">
                {user?.firstName || user?.FirstName} {user?.lastName || user?.LastName}
              </div>
              <div className="sidebar-user-role">
                {user?.role || user?.Role}
              </div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            🚪 Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="main-area">
        {/* Topbar */}
        <header className="topbar">
          <div className="topbar-left">
            <h2>{title}</h2>
            {sub && <p>{sub}</p>}
          </div>
          <div className="topbar-right">
            <button
              className="notif-btn"
              onClick={() => navigate(notifPath)}
              title="Notifications"
            >
              🔔
              {unread > 0 && (
                <span className="notif-badge">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>
            <div className="topbar-user">
              <div className="topbar-avatar">{initials}</div>
              <div>
                <div className="topbar-user-name">
                  {user?.firstName || user?.FirstName} {user?.lastName || user?.LastName}
                </div>
                <div className="topbar-user-role">
                  {user?.role || user?.Role}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content rendered here */}
        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}